import "./style.css";
import ApexCharts from "apexcharts";
import { apiService, SLUG_TO_ID, type Stock, type Market, type TracksResponse, type AllScoresResponse } from "./api";
import {
  renderInvestorAnalysis,
  renderTrackScore,
  renderAllScores,
  extractTracksAndRisk,
  renderTopStocks,
  renderDisqualifiers,
  renderPortfolioRules,
  renderLoginView,
  setViewMarket,
  chartColourFor,
} from "./views";
import { authService } from "./auth";

const MARKET_SAMPLES: Record<Market, { tickers: string[]; placeholder: string }> = {
  india: {
    tickers: ["INFY", "TCS", "RELIANCE", "HDFCBANK"],
    placeholder: "Search stocks (e.g., INFY, TCS, RELIANCE)...",
  },
  us: {
    tickers: ["AAPL", "MSFT", "GOOGL", "JPM"],
    placeholder: "Search stocks (e.g., AAPL, MSFT, GOOGL)...",
  },
};

// Application state
class ValueInvestingApp {
  private stocks: Stock[] = [];
  private filteredStocks: Stock[] = [];
  private selectedStock: Stock | null = null;
  private autocompleteIndex: number = -1;
  private selectedTrack: string = "deep_value";
  private grahamRuleSet: string = "graham_defensive";
  private activeCharts: ApexCharts[] = [];
  private selectedMarket: Market = "india";

  // DOM elements
  private elements = {
    menuToggle: document.getElementById("menu-toggle")!,
    backBtn: document.getElementById("back-btn")!,
    sidebar: document.getElementById("sidebar")!,
    searchInput: document.getElementById("stock-search") as HTMLInputElement,
    autocompleteList: document.getElementById("autocomplete-list")!,
    generateBtn: document.getElementById(
      "generate-report-btn",
    ) as HTMLButtonElement,
    resetBtn: document.getElementById("reset-btn") as HTMLButtonElement,
    selectedStockInfo: document.getElementById("selected-stock-info")!,
    contentPanel: document.getElementById("content-panel")!,
    loadingOverlay: document.getElementById("loading-overlay")!,
    navItems: document.querySelectorAll(".nav-item"),
    userProfile: document.getElementById("user-profile")!,
    userAvatar: document.getElementById("user-avatar") as HTMLImageElement,
    userName: document.getElementById("user-name")!,
    logoutBtn: document.getElementById("logout-btn")!,
  };

  private initialWelcomeHTML: string = "";

  constructor() {
    this.init();
  }

  private async init() {
    console.log("🚀 Initializing app...");
    console.log("📍 Nav items found:", this.elements.navItems.length);

    // Store initial welcome screen HTML
    this.initialWelcomeHTML = this.elements.contentPanel.innerHTML;

    // Initialize auth service
    try {
      await authService.init();
      this.updateUserUI();
    } catch (error) {
      console.error("Failed to initialize auth service:", error);
    }

    // Debug: Test if clicks work at all on sidebar
    document.addEventListener(
      "click",
      (e) => {
        const target = e.target as HTMLElement;
        if (target.closest(".nav-item")) {
          console.log("🎯 GLOBAL CLICK DETECTED on nav-item:", target);
        }
      },
      true,
    );

    this.setupEventListeners();
    this.initMarket();
    await this.loadStocks();
    this.checkAPIHealth();
  }

  /**
   * Market selection (US / India). Persisted in sessionStorage; drives which
   * universe every API call targets and how currency is formatted.
   */
  private initMarket() {
    const saved = sessionStorage.getItem("selectedMarket");
    this.selectedMarket = saved === "us" ? "us" : "india";
    apiService.setMarket(this.selectedMarket);
    setViewMarket(this.selectedMarket);
    this.updateMarketToggleUI();
    this.updateHeroSamples();
  }

  /** Swap the search placeholder + "Try:" chips for the selected market. */
  private updateHeroSamples() {
    const { tickers, placeholder } = MARKET_SAMPLES[this.selectedMarket];
    this.elements.searchInput.placeholder = placeholder;
    const container = document.getElementById("hero-quick-chips");
    if (!container) return;
    const chipsHtml = tickers
      .map((t) => `<button type="button" class="hero-chip" data-quick="${t}">${t}</button>`)
      .join("");
    container.innerHTML = `<span class="hero-hint-label">Try:</span>${chipsHtml}`;
  }

  private updateMarketToggleUI() {
    document.querySelectorAll<HTMLElement>("#market-toggle .market-btn").forEach((btn) => {
      btn.classList.toggle("active", btn.getAttribute("data-market") === this.selectedMarket);
    });
  }

  private async setMarket(market: Market) {
    if (market === this.selectedMarket) return;
    this.selectedMarket = market;
    sessionStorage.setItem("selectedMarket", market);
    apiService.setMarket(market);
    setViewMarket(market);
    this.updateMarketToggleUI();
    this.updateHeroSamples();

    // Tickers differ per market, so clear the current selection and reload.
    this.selectedStock = null;
    this.elements.searchInput.value = "";
    this.elements.generateBtn.disabled = true;
    this.elements.selectedStockInfo.classList.add("hidden");
    this.hideAutocomplete();
    await this.loadStocks();

    // Refresh the rankings if they are showing; otherwise return to welcome.
    const active = document.querySelector(".nav-item.active");
    if (active && active.getAttribute("data-view") === "top-stocks") {
      this.loadAnalysis("top-stocks");
    } else {
      this.elements.contentPanel.innerHTML = this.initialWelcomeHTML;
      this.elements.navItems.forEach((i) => i.classList.remove("active"));
    }
  }

  /** Market-aware market-cap label (₹ crore for India, USD for US). */
  private formatCap(v: number): string {
    if (this.selectedMarket === "us") {
      if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}T`;
      if (v >= 1_000) return `$${(v / 1_000).toFixed(2)}B`;
      return `$${v.toFixed(0)}M`;
    }
    return `₹${v.toFixed(2)} Cr`;
  }

  /**
   * Setup all event listeners
   */
  private setupEventListeners() {
    // Mobile menu toggle
    this.elements.menuToggle.addEventListener("click", () => {
      this.elements.sidebar.classList.toggle("open");
    });

    // Back button
    this.elements.backBtn.addEventListener("click", () => {
      if (this.selectedStock) {
        this.loadAnalysis("tracks");
      }
    });

    // Market toggle (US / India)
    document.querySelectorAll<HTMLElement>("#market-toggle .market-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const m = btn.getAttribute("data-market") as Market | null;
        if (m) this.setMarket(m);
      });
    });

    // Track toggle (Deep Value / Quality Compounder) for leaderboards
    document.querySelectorAll("[data-track]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const track = btn.getAttribute("data-track");
        if (!track) return;
        this.selectedTrack = track;
        document.querySelectorAll("[data-track]").forEach((b) => b.classList.remove("active"));
        btn.classList.add("active");
        const active = document.querySelector(".nav-item.active");
        if (active && active.getAttribute("data-view") === "top-stocks") {
          this.loadAnalysis("top-stocks");
        }
      });
    });

    // Click outside sidebar to close on mobile
    document.addEventListener("click", (e) => {
      if (window.innerWidth < 1024) {
        const target = e.target as HTMLElement;
        if (
          !this.elements.sidebar.contains(target) &&
          !this.elements.menuToggle.contains(target) &&
          this.elements.sidebar.classList.contains("open")
        ) {
          this.elements.sidebar.classList.remove("open");
        }
      }
    });

    // Search input
    this.elements.searchInput.addEventListener("input", () => {
      this.handleSearch();
    });

    // Keyboard navigation for autocomplete
    this.elements.searchInput.addEventListener("keydown", (e) => {
      this.handleAutocompleteKeyboard(e);
    });

    // Generate report button
    this.elements.generateBtn.addEventListener("click", () => {
      this.generateReport();
    });

    // Reset button
    this.elements.resetBtn.addEventListener("click", () => {
      this.resetApp();
    });

    // Quick-search chips in the hero (e.g. "INFY", "TCS"). Delegated from the
    // container since updateHeroSamples() regenerates the chips per market.
    document.getElementById("hero-quick-chips")?.addEventListener("click", (e) => {
      const chip = (e.target as HTMLElement).closest<HTMLElement>(".hero-chip");
      const ticker = chip?.getAttribute("data-quick");
      if (!ticker) return;
      const stock = this.stocks.find(
        (s) => s.ticker.toUpperCase() === ticker.toUpperCase(),
      );
      if (stock) {
        this.hideAutocomplete();
        this.selectStock(stock);
        this.generateReport();
      } else {
        // Stocks not loaded yet or ticker absent — fall back to typing it in
        this.elements.searchInput.value = ticker;
        this.handleSearch();
      }
    });

    // Mobile Top Stocks shortcut button
    const topStocksMobileBtn = document.getElementById("top-stocks-mobile-btn");
    if (topStocksMobileBtn) {
      topStocksMobileBtn.addEventListener("click", () => {
        this.loadAnalysis("top-stocks");
        // Scroll content into view on mobile
        this.elements.contentPanel.scrollIntoView({ behavior: "smooth" });
      });
    }

    // Logout button
    this.elements.logoutBtn.addEventListener("click", () => {
      this.handleLogout();
    });

    // Navigation items
    console.log(
      "🔧 Setting up nav item event listeners for",
      this.elements.navItems.length,
      "items",
    );
    this.elements.navItems.forEach((item, index) => {
      console.log(
        `   ➡️ Adding listener to item ${index}:`,
        item.getAttribute("data-view"),
      );
      item.addEventListener(
        "click",
        (e) => {
          console.log("🔵 Nav item clicked!", e);
          const view = item.getAttribute("data-view");
          const noStockRequired = item.getAttribute("data-no-stock-required") === "true";
          console.log("📌 View attribute:", view);
          console.log("📌 Selected stock:", this.selectedStock);
          console.log("📌 No stock required:", noStockRequired);
          
          if (view && (this.selectedStock || noStockRequired)) {
            console.log("✅ Calling loadAnalysis with:", view);
            this.loadAnalysis(view);
            // Close mobile menu
            if (window.innerWidth < 1024) {
              this.elements.sidebar.classList.remove("open");
            }
          } else {
            console.log(
              "❌ Cannot load analysis - view:",
              view,
              "stock:",
              this.selectedStock,
            );
            if (!this.selectedStock && !noStockRequired) {
              alert("Please select a stock and generate report first");
            }
          }
        },
        true,
      );

      // Test that the element is clickable
      (item as HTMLElement).style.cursor = "pointer";
    });
  }

  /**
   * Load all available stocks
   */
  private async loadStocks() {
    try {
      this.stocks = await apiService.getStocks();
      console.log(`Loaded ${this.stocks.length} stocks`);
    } catch (error) {
      console.error("Failed to load stocks:", error);
      this.showError(
        "Failed to load stock list. Please ensure the API server is running.",
      );
    }
  }

  /**
   * Check API health
   */
  private async checkAPIHealth() {
    try {
      const health = await apiService.healthCheck();
      console.log("API Status:", health);
    } catch (error) {
      console.error("API is not reachable:", error);
      this.showError(
        "Cannot connect to API server. Please start the backend service.",
      );
    }
  }

  /**
   * Handle search input
   */
  private handleSearch() {
    const query = this.elements.searchInput.value.trim().toLowerCase();

    if (query.length < 1) {
      this.hideAutocomplete();
      return;
    }

    // Filter stocks
    this.filteredStocks = this.stocks.filter(
      (stock) =>
        stock.ticker.toLowerCase().includes(query) ||
        stock.company_name.toLowerCase().includes(query),
    );

    this.showAutocomplete();
    this.autocompleteIndex = -1;
  }

  /**
   * Show autocomplete dropdown
   */
  private showAutocomplete() {
    if (this.filteredStocks.length === 0) {
      this.elements.autocompleteList.innerHTML = `
        <div style="padding: 1rem; text-align: center; color: var(--text-muted);">
          No stocks found
        </div>
      `;
      this.elements.autocompleteList.classList.remove("hidden");
      return;
    }

    const html = this.filteredStocks
      .slice(0, 10)
      .map(
        (stock, index) => `
      <div class="autocomplete-item ${index === this.autocompleteIndex ? "active" : ""}" data-index="${index}">
        <div class="autocomplete-ticker">${stock.ticker}</div>
        <div class="autocomplete-name">${stock.company_name}</div>
        <div class="autocomplete-sector">${stock.sector}</div>
      </div>
    `,
      )
      .join("");

    this.elements.autocompleteList.innerHTML = html;
    this.elements.autocompleteList.classList.remove("hidden");

    // Add click handlers
    this.elements.autocompleteList
      .querySelectorAll(".autocomplete-item")
      .forEach((item) => {
        item.addEventListener("click", () => {
          const index = parseInt(item.getAttribute("data-index") || "0");
          this.selectStock(this.filteredStocks[index]);
        });
      });
  }

  /**
   * Hide autocomplete
   */
  private hideAutocomplete() {
    this.elements.autocompleteList.classList.add("hidden");
  }

  /**
   * Handle keyboard navigation in autocomplete
   */
  private handleAutocompleteKeyboard(e: KeyboardEvent) {
    if (this.filteredStocks.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        this.autocompleteIndex = Math.min(
          this.autocompleteIndex + 1,
          this.filteredStocks.length - 1,
        );
        this.showAutocomplete();
        break;

      case "ArrowUp":
        e.preventDefault();
        this.autocompleteIndex = Math.max(this.autocompleteIndex - 1, -1);
        this.showAutocomplete();
        break;

      case "Enter":
        e.preventDefault();
        if (this.autocompleteIndex >= 0) {
          this.selectStock(this.filteredStocks[this.autocompleteIndex]);
        }
        break;

      case "Escape":
        this.hideAutocomplete();
        break;
    }
  }

  /**
   * Select a stock
   */
  private selectStock(stock: Stock) {
    this.selectedStock = stock;
    this.elements.searchInput.value = `${stock.ticker} - ${stock.company_name}`;
    this.hideAutocomplete();

    // Enable generate button
    this.elements.generateBtn.disabled = false;

    // Show selected stock info
    this.elements.selectedStockInfo.innerHTML = `
      <h3 style="color: var(--text-primary); font-size: 1rem;">${stock.company_name} (${stock.ticker})</h3>
      <p style="color: var(--text-secondary); font-size: 0.875rem; margin-top: 0.25rem;">
        ${stock.sector} • Market Cap: ${this.formatCap(stock.market_cap_cr)}
      </p>
    `;
    this.elements.selectedStockInfo.classList.remove("hidden");

    console.log("Selected stock:", stock);
  }

  /**
   * Generate report
   */
  private async generateReport() {
    console.log("🔵 Generate Report clicked!");
    if (!this.selectedStock) {
      console.log("❌ No stock selected");
      return;
    }
    console.log("✅ Selected stock:", this.selectedStock.ticker);

    // Load the two-track score by default
    await this.loadAnalysis("tracks");
  }

  /**
   * Load specific analysis
   */
  private async loadAnalysis(view: string) {
    console.log("🟢 loadAnalysis called with view:", view);
    
    // Special case: top-stocks doesn't require a selected stock
    if (view !== "top-stocks" && !this.selectedStock) {
      console.log("❌ No stock selected in loadAnalysis");
      alert("Please select a stock first");
      return;
    }
    
    if (this.selectedStock) {
      console.log("✅ Loading analysis for:", this.selectedStock.ticker);
    }

    this.updateActiveNavItem(view);
    this.showLoading(true);

    try {
      let content = "";
      let mountedTracksData: TracksResponse | null = null;
      let mountedAllScores: AllScoresResponse | null = null;
      console.log("📊 Fetching data from API for view:", view);

      if (view === "top-stocks") {
        if (!authService.isAuthenticated()) {
          content = renderLoginView();
          this.elements.backBtn.classList.add("hidden");
          setTimeout(() => {
            const loginBtn = document.getElementById("google-signin-btn");
            if (loginBtn) {
              loginBtn.addEventListener("click", async () => {
                try {
                  loginBtn.textContent = "Signing in...";
                  loginBtn.setAttribute("disabled", "true");
                  await authService.signIn();
                  this.updateUserUI();
                  this.loadAnalysis("top-stocks");
                } catch (error) {
                  console.error("Login failed:", error);
                  alert("Failed to sign in. Please try again.");
                  loginBtn.textContent = "Sign in with Google";
                  loginBtn.removeAttribute("disabled");
                }
              });
            }
          }, 0);
        } else {
          const topStocksData = await apiService.getTopStocks(this.selectedTrack, 100);
          content = renderTopStocks(topStocksData);
          this.elements.backBtn.classList.add("hidden");
          setTimeout(() => {
            this.setupTopStocksClickHandlers();
            this.setupRefreshButton();
            this.setupExportButton();
            this.setupBottomTrack();
          }, 0);
        }
      } else if (view === "tracks" || view === "composite") {
        const [tracksData, allScores] = await Promise.all([
          apiService.getTracks(this.selectedStock!.ticker),
          apiService.getAllScores(this.selectedStock!.ticker).catch(() => null),
        ]);
        content = renderTrackScore(tracksData, allScores?.investors);
        mountedTracksData = tracksData;
        this.elements.backBtn.classList.add("hidden");
      } else if (view === "disqualifiers") {
        const dqData = await apiService.getDisqualifiers(this.selectedStock!.ticker);
        content = renderDisqualifiers(dqData);
        this.elements.backBtn.classList.add("hidden");
      } else if (view === "sell-rules") {
        const [sellData, sizingData] = await Promise.all([
          apiService.getSellRules(),
          apiService.getPositionSizing(),
        ]);
        content = renderPortfolioRules(sellData.sell_rules, sizingData);
        this.elements.backBtn.classList.add("hidden");
      } else if (view === "all-scores") {
        const allScoresData = await apiService.getAllScores(this.selectedStock!.ticker);
        content = renderAllScores(allScoresData);
        mountedAllScores = allScoresData;
        this.elements.backBtn.classList.add("hidden");
      } else if (SLUG_TO_ID[view]) {
        const investorId = SLUG_TO_ID[view];
        const ruleSet = investorId === "graham" ? this.grahamRuleSet : undefined;
        const investorData = await apiService.getInvestorAnalysis(
          view,
          this.selectedStock!.ticker,
          ruleSet,
        );
        content = renderInvestorAnalysis(investorData);
        this.elements.backBtn.classList.remove("hidden");
      } else {
        content = "<p>Unknown view</p>";
      }

      this.destroyCharts();
      this.elements.contentPanel.innerHTML = content;

      // Wire the Graham Defensive/Enterprising rule-set selector after render
      if (SLUG_TO_ID[view] === "graham") {
        this.setupGrahamRuleSetTabs();
      }
      if (mountedTracksData) {
        const data = mountedTracksData;
        setTimeout(() => this.mountTrackCharts(data), 0);
      }
      if (mountedAllScores) {
        const { dv, qc, risk } = extractTracksAndRisk(mountedAllScores);
        setTimeout(() => {
          this.mountGauge("gauge-as-deep_value", dv?.composite);
          this.mountGauge("gauge-as-quality_compounder", qc?.composite);
          this.mountGauge("gauge-as-risk", risk?.score);
        }, 0);
      }
    } catch (error) {
      console.error("❌ Failed to load analysis:", error);
      this.showError(
        "Failed to load analysis. Please check if the backend API is running and the stock data is available.",
      );
    } finally {
      this.showLoading(false);
    }
  }

  /**
   * Update active navigation item
   */
  private updateActiveNavItem(view: string) {
    this.elements.navItems.forEach((item) => {
      if (item.getAttribute("data-view") === view) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
  }

  /**
   * Show/hide loading overlay
   */
  private showLoading(show: boolean) {
    if (show) {
      this.elements.loadingOverlay.classList.remove("hidden");
    } else {
      this.elements.loadingOverlay.classList.add("hidden");
    }
  }

  /**
   * Setup click handlers for top stocks table rows
   */
  private setupTopStocksClickHandlers() {
    const stockRows = document.querySelectorAll(".stock-row");
    stockRows.forEach((row) => {
      row.addEventListener("click", () => {
        const ticker = row.getAttribute("data-ticker");
        if (ticker) {
          // Find the stock in our stocks list
          const stock = this.stocks.find((s) => s.ticker === ticker);
          if (stock) {
            this.selectStock(stock);
            // Automatically show the composite score for the selected stock
            this.generateReport();
          } else {
            alert(`Stock ${ticker} not found in the stock list`);
          }
        }
      });
    });
  }

  /**
   * Setup refresh button for top stocks view
   */
  private setupRefreshButton() {
    const refreshBtn = document.getElementById("refresh-top-stocks-btn");
    if (refreshBtn) {
      refreshBtn.addEventListener("click", async () => {
        // Disable button during refresh
        refreshBtn.setAttribute("disabled", "true");
        refreshBtn.innerHTML = '<span style="font-size: 1.2rem;">⏳</span><span>Refreshing...</span>';
        
        try {
          // Fetch with refresh=true to force recalculation
          this.showLoading(true);
          const topStocksData = await apiService.getTopStocks(this.selectedTrack, 100, true);
          const content = renderTopStocks(topStocksData);
          this.elements.contentPanel.innerHTML = content;
          
          // Re-setup handlers for the new content
          setTimeout(() => {
            this.setupTopStocksClickHandlers();
            this.setupRefreshButton();
            this.setupExportButton();
            this.setupBottomTrack();
          }, 0);
          
          this.showLoading(false);
        } catch (error) {
          this.showLoading(false);
          this.showError(`Failed to refresh top stocks: ${error}`);
        } finally {
          // Re-enable button
          refreshBtn.removeAttribute("disabled");
          refreshBtn.innerHTML = '<span style="font-size: 1.2rem;">🔄</span><span>Refresh Rankings</span>';
        }
      });
    }
  }

  /**
   * Wire the "Export all" button — downloads the full ranking of ALL stocks
   * (both tracks) for the current market as an .xlsx workbook.
   */
  private setupExportButton() {
    const btn = document.getElementById("export-rankings-btn");
    if (!btn) return;
    btn.addEventListener("click", () => {
      const a = document.createElement("a");
      a.href = apiService.exportRankingsUrl();
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
  }

  /**
   * Destroy any ApexCharts instances mounted by the previous view. Must run
   * before the content panel's innerHTML is replaced, or chart DOM nodes are
   * orphaned (memory leak) and stale instances keep listening for resizes.
   */
  private destroyCharts() {
    this.activeCharts.forEach((c) => {
      try { c.destroy(); } catch (_) { /* already gone */ }
    });
    this.activeCharts = [];
  }

  /** One solid-gauge chart (Deep Value / Quality Compounder / Risk Overlay). */
  private mountGauge(containerId: string, score: number | null | undefined) {
    const el = document.getElementById(containerId);
    if (!el) return;
    const value = score === null || score === undefined ? null : score;
    const pct = value === null ? 0 : Math.max(0, Math.min(100, value * 10));
    const colour = chartColourFor(value);
    const chart = new ApexCharts(el, {
      chart: { type: "radialBar", height: 170, sparkline: { enabled: true }, animations: { enabled: true } },
      series: [pct],
      colors: [colour],
      plotOptions: {
        radialBar: {
          hollow: { size: "56%" },
          track: { background: "#eef1f5" },
          dataLabels: {
            name: { show: false },
            value: {
              fontSize: "26px", fontWeight: 800, color: "#1f2a37", offsetY: 9,
              formatter: () => (value === null ? "n/a" : value.toFixed(1)),
            },
          },
        },
      },
      stroke: { lineCap: "round" },
      tooltip: { enabled: false },
    });
    chart.render();
    this.activeCharts.push(chart);
  }

  /** Horizontal bar chart of a track's dimension scores, coloured by strength. */
  private mountDimensionBar(containerId: string, dims: { label: string; score: number | null }[]) {
    const el = document.getElementById(containerId);
    if (!el || dims.length === 0) return;
    const values = dims.map((d) => d.score ?? 0);
    const colours = dims.map((d) => chartColourFor(d.score));
    const chart = new ApexCharts(el, {
      chart: { type: "bar", height: dims.length * 48 + 30, toolbar: { show: false }, animations: { enabled: true } },
      series: [{ name: "Score", data: values }],
      colors: colours,
      plotOptions: {
        bar: { horizontal: true, distributed: true, borderRadius: 6, barHeight: "58%" },
      },
      dataLabels: {
        enabled: true,
        formatter: (v: number, opts?: any) =>
          dims[opts?.dataPointIndex]?.score === null ? "—" : Number(v).toFixed(1),
        style: { fontSize: "12px", fontWeight: 700, colors: ["#1f2a37"] },
        offsetX: 10,
      },
      xaxis: {
        categories: dims.map((d) => d.label), max: 10,
        labels: { style: { fontSize: "11px" } },
      },
      grid: { yaxis: { lines: { show: false } } },
      legend: { show: false },
      tooltip: { enabled: false },
    });
    chart.render();
    this.activeCharts.push(chart);
  }

  /** Mount every chart on the Two-Track Value Score report. */
  private mountTrackCharts(data: TracksResponse) {
    const dv = data.tracks["deep_value"];
    const qc = data.tracks["quality_compounder"];
    const risk = data.risk_overlay;

    this.mountGauge("gauge-deep_value", dv?.composite);
    this.mountGauge("gauge-quality_compounder", qc?.composite);
    this.mountGauge("gauge-risk", risk?.score);

    // "Score at a glance" — three separate small rings (same gauge component).
    this.mountGauge("glance-ring-deep_value", dv?.composite);
    this.mountGauge("glance-ring-quality_compounder", qc?.composite);
    this.mountGauge("glance-ring-risk", risk?.score);

    if (dv) this.mountDimensionBar("stackbar-deep_value", dv.dimensions.map((d) => ({ label: d.label, score: d.score })));
    if (qc) this.mountDimensionBar("stackbar-quality_compounder", qc.dimensions.map((d) => ({ label: d.label, score: d.score })));
  }

  /**
   * Wire the Deep Value / Quality Compounder selector rendered below the cards.
   */
  private setupBottomTrack() {
    document.querySelectorAll<HTMLElement>(".ts-track-toggle .ts-track-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        const track = btn.getAttribute("data-track");
        if (!track || track === this.selectedTrack) return;
        this.selectedTrack = track;
        // Keep the sidebar toggle in sync with the bottom selector.
        document.querySelectorAll("[data-track]").forEach((b) =>
          b.classList.toggle("active", b.getAttribute("data-track") === track),
        );
        this.loadAnalysis("top-stocks");
      });
    });
  }

  /**
   * Wire the Graham Defensive/Enterprising rule-set selector
   */
  private setupGrahamRuleSetTabs() {
    const tabs = document.querySelectorAll<HTMLElement>("[data-graham-ruleset]");
    tabs.forEach((tab) => {
      const rs = tab.getAttribute("data-graham-ruleset");
      if (rs === this.grahamRuleSet) tab.classList.add("active");
      tab.addEventListener("click", () => {
        if (!rs || rs === this.grahamRuleSet) return;
        this.grahamRuleSet = rs;
        this.loadAnalysis("benjamin-graham");
      });
    });
  }

  /**
   * Show error message
   */
  private showError(message: string) {
    this.elements.contentPanel.innerHTML = `
      <div style="text-align: center; padding: 3rem 2rem;">
        <div style="font-size: 3rem; margin-bottom: 1rem;">⚠️</div>
        <h2 style="color: var(--danger); margin-bottom: 1rem;">Error</h2>
        <p style="color: var(--text-secondary); max-width: 500px; margin: 0 auto;">
          ${message}
        </p>
      </div>
    `;
  }

  /**
   * Update user profile UI
   */
  private updateUserUI() {
    const user = authService.getCurrentUser();
    
    if (user) {
      this.elements.userAvatar.src = user.picture;
      this.elements.userName.textContent = user.name;
      this.elements.userProfile.classList.remove("hidden");
    } else {
      this.elements.userProfile.classList.add("hidden");
    }
  }

  /**
   * Handle user logout
   */
  private handleLogout() {
    authService.signOut();
    this.updateUserUI();
    
    // If currently viewing Top Stocks, redirect to welcome screen
    const activeView = document.querySelector(".nav-item.active");
    if (activeView && activeView.getAttribute("data-view") === "top-stocks") {
      this.resetApp();
    }
    
    alert("You have been logged out successfully.");
  }

  /**
   * Reset app to initial state
   */
  private resetApp() {
    console.log("🔄 Resetting app to initial state...");
    
    // Reset state
    this.selectedStock = null;
    this.filteredStocks = [];
    this.autocompleteIndex = -1;
    
    // Reset UI
    this.elements.searchInput.value = "";
    this.elements.generateBtn.disabled = true;
    this.elements.selectedStockInfo.classList.add("hidden");
    this.elements.autocompleteList.classList.add("hidden");
    this.elements.backBtn.classList.add("hidden");
    this.elements.sidebar.classList.remove("open");
    
    // Restore welcome screen
    this.elements.contentPanel.innerHTML = this.initialWelcomeHTML;
    
    // Clear active nav items
    this.elements.navItems.forEach(item => item.classList.remove("active"));
    
    console.log("✅ App reset complete");
  }
}

// Initialize app when DOM is ready
let appInstance: ValueInvestingApp | null = null;

document.addEventListener("DOMContentLoaded", () => {
  appInstance = new ValueInvestingApp();
  console.log("Value Investing Analysis App initialized", appInstance);
});

// Global utility function for toggling metrics tables (dimension detail)
(window as any).toggleMetricsTable = function (headerElement: HTMLElement) {
  // Content may be the next sibling (legacy) or elsewhere within the .dim wrapper
  const wrap = headerElement.closest(".dim") || headerElement.parentElement;
  let content =
    (wrap && (wrap.querySelector(".metrics-table-content") as HTMLElement)) ||
    (headerElement.nextElementSibling as HTMLElement);

  if (content && content.classList.contains("metrics-table-content")) {
    const isCollapsed = content.classList.contains("collapsed");
    if (isCollapsed) {
      content.classList.remove("collapsed");
      content.classList.add("expanded");
      headerElement.classList.add("expanded");
    } else {
      content.classList.remove("expanded");
      content.classList.add("collapsed");
      headerElement.classList.remove("expanded");
    }
  }
};

// Global function for navigating to investor analysis from composite score cards
(window as any).navigateToInvestorAnalysis = function (investorView: string) {
  console.log("🎯 Navigate to investor analysis:", investorView);
  if (appInstance) {
    // Call the loadAnalysis method directly
    (appInstance as any).loadAnalysis(investorView);
  } else {
    console.error("❌ App instance not available");
  }
};
